import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../app.environments';
import {
  UpdateRolePermissionResponse,
  RolePermissions,
  Role,
  RoleResponse,
  SingleRoleResponse
} from '../models/role.model';

@Injectable({
  providedIn: 'root'
})
export class RoleService {
  private apiUrl = `${environment.apiURL}/hr/roles`;
  private userUrl = `${environment.apiURL}/hr/users`;
  private SettingapiUrl = `${environment.apiURL}/chead/products`;
  private clientSettingUrl = `${environment.apiURL}/chead/clients`;

  constructor(private http: HttpClient) { }

  /**
   * Get role permission matrix by role ID
   */
  getRolePermissionMatrix(roleID: number): Observable<UpdateRolePermissionResponse> {
    return this.http.get<UpdateRolePermissionResponse>(`${this.apiUrl}/rolespermissionmatrix/${roleID}`);
  }

  getClientData(client_id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/clientdata/${client_id}`);
  }
  /**
   * Get ClientSettings by client ID
   */
  getClientSettings(client_id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/checksettings/${client_id}`);
  }


  /**
   * Update multiple permissions at once
   */
  updateMultiplePermissions(requests: RolePermissions[], roleID: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/updatePrivileges/${roleID}`, requests);
  }

  /**
   * Get all roles with pagination and search
   */
  getAll(filters: { search: string, page: number, size: number, client_id: number }): Observable<RoleResponse> {
    const params = new HttpParams()
      .set('search', filters.search)
      .set('page', filters.page.toString())
      .set('size', filters.size.toString())
      .set('client_id', filters.client_id)
    return this.http.get<RoleResponse>(`${this.apiUrl}`, { params });
  }

  /**
   * Create new role
   */
  createRole(role: Partial<Role>): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}`, role);
  }

  /**
   * Update existing role by UUID
   */
  updateRole(uuid: string, role: Partial<Role>): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${uuid}`, role);
  }

  /**
   * Get role by UUID
   */
  getById(uuid: string): Observable<SingleRoleResponse> {
    return this.http.get<SingleRoleResponse>(`${this.apiUrl}/${uuid}`);
  }


  changePassword(userData: any): Observable<any> {
    return this.http.put<any>(this.userUrl + '/changepwd', userData);
  }
  /**
   * Delete role by UUID
   */
  deleteRole(uuid: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${uuid}`);
  }

  /**
   * Get role name from role ID (fallback method for backward compatibility)
   * Note: This will be replaced with API-based role lookup in the future
   */
  getRoleName(roleId: number): string {
    const roleNames: { [key: number]: string } = {
      0: 'Super Admin',
      1: 'Admin',
      2: 'Store Manager',
      3: 'Product Manager',
      4: 'Purchaser',
      5: 'Warehouse Manager',
      6: 'Cashier',
      7: 'Viewers'
    };
    return roleNames[roleId] || `Role ${roleId}`;
  }

  /**
   * Check if user has super admin role
   */
  isSuperAdmin(roleId: number): boolean {
    return roleId === 0; // Super Admin role
  }

  /**
   * Check if user has admin role (including super admin)
   */
  isAdmin(roleId: number): boolean {
    return roleId === 1; // Super Admin, Admin, or Store Manager
  }

  /**
   * Check if user has viewer role
   */
  isViewer(roleId: number): boolean {
    return roleId === 7; // Viewers role
  }

  /**
   * Set API URL (for backward compatibility)
   */
  setApiUrl(url: string): void {
    this.apiUrl = url;
  }

  getGensettings(): Observable<any> {
    return this.http.get<any>(`${this.userUrl}/checkgensettings`);
  }

  getUserProfile(uuid: string): Observable<any> {
    return this.http.get<any>(`${this.userUrl}/userprofile/${uuid}`);
  }

  getClientDetail(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/checkClientDetail`);
  }
}
